import { useAuth } from '../../lib/auth';
import { signInWithKakao } from '../../lib/supabase';
import { Button } from '@/components/ui/button';
import { OnboardingForm } from './onboarding-form';
import { useMe } from './use-me';

/**
 * Gate status for a change action (vote/comment/write), driven by login +
 * onboarding state (, AC-0.2/0.3).
 *
 *   'loading'    — auth/profile still resolving
 *   'anonymous'  — signed out → prompt Kakao login
 *   'onboarding' — signed in but company name not set → require onboarding
 *   'ready'      — logged in + onboarded → action allowed
 */
export type GateStatus = 'loading' | 'anonymous' | 'onboarding' | 'ready';

export function useActionGate(): GateStatus {
  const { isAuthenticated, isLoading } = useAuth();
  const me = useMe();

  if (isLoading) return 'loading';
  if (!isAuthenticated) return 'anonymous';
  if (me.isLoading || me.data === undefined) return 'loading';
  return me.data.onboarded ? 'ready' : 'onboarding';
}

/**
 * Inline prompt rendered in place of a change-action UI when the gate is not
 * 'ready'. Login → onboarding, in order. Used by the vote panel; reused by
 * comment/write entry points in later stages.
 */
export function GatePrompt({
  status,
  message,
}: {
  status: Exclude<GateStatus, 'ready'>;
  message: string;
}) {
  if (status === 'loading') {
    return <p className="text-sm text-text-muted">불러오는 중…</p>;
  }

  if (status === 'anonymous') {
    return (
      <div className="flex flex-col items-center gap-3 rounded-lg border border-border bg-surface p-6 text-center">
        <p className="text-sm text-text">{message}</p>
        <Button
          type="button"
          onClick={() => void signInWithKakao()}
          className="bg-[#FEE500] text-[#191919] hover:bg-[#FDD835]"
        >
          카카오로 시작하기
        </Button>
      </div>
    );
  }

  // status === 'onboarding'
  return <OnboardingForm />;
}
